import { useCallback, useEffect, useMemo, useState } from "react";
import { getKiosks } from "../services/KioskService";
import {
  getKioskTransactionsHistory,
  getKioskTransactionsSummary,
} from "../services/KioskTransactionsService";
import { Kiosk } from "../types/kiosk.type";
import {
  KioskPerformanceFilters,
  KioskTransactionStatus,
  KioskTransactionsSummary,
  KioskTransactionHistoryRow,
  KioskTransactionsPagination,
} from "../types/kioskTransactions.type";

const defaultPagination: KioskTransactionsPagination = {
  page: 1,
  limit: 10,
  total: 0,
  totalPages: 1,
};

const emptySummary: KioskTransactionsSummary = {
  kpis: {
    totalTransactions: 0,
    successful: 0,
    failed: 0,
    pending: 0,
    totalReceived: 0,
    totalUnits: 0,
  },
  impact: {
    totalFoodBars: 0,
    totalAmountUsd: 0,
    byChannel: {
      DIGITAL: { foodBars: 0, amountUsd: 0 },
      PHYSICAL: { foodBars: 0, amountUsd: 0 },
    },
  },
  trends: {
    daily: [],
    weekly: [],
    monthly: [],
  },
  topDonors: [],
  ownerRewards: {
    totalRewardsUsd: 0,
    totalPayableUsd: 0,
    totalPayoutUsd: 0,
    balanceUsd: 0,
  },
  ownerPreferences: {
    reportLiveEnabled: false,
    reportWeeklyEnabled: true,
    reportMonthlyEnabled: false,
    payoutMode: "PROGRAM",
    payoutProgramKey: "humanitarian-food-programmes",
    payoutProgramName: "Humanitarian food programmes",
  },
  perKioskPerformance: [],
};

export interface KioskTransactionsDraftFilters {
  kioskId: string;
  status: "" | KioskTransactionStatus;
  fromDate: string;
  toDate: string;
}

const toIsoDateStart = (value: string): string | undefined => {
  if (!value) {
    return undefined;
  }
  const date = new Date(`${value}T00:00:00.000Z`);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const toIsoDateEnd = (value: string): string | undefined => {
  if (!value) {
    return undefined;
  }
  const date = new Date(`${value}T23:59:59.999Z`);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const toApiFilters = (
  filters: KioskTransactionsDraftFilters,
): KioskPerformanceFilters => ({
  kioskId: filters.kioskId || undefined,
  status: filters.status || undefined,
  fromDate: toIsoDateStart(filters.fromDate),
  toDate: toIsoDateEnd(filters.toDate),
});

export const useKioskTransactions = () => {
  const [kiosks, setKiosks] = useState<Kiosk[]>([]);
  const [summary, setSummary] = useState<KioskTransactionsSummary>(emptySummary);
  const [transactions, setTransactions] = useState<KioskTransactionHistoryRow[]>([]);
  const [pagination, setPagination] =
    useState<KioskTransactionsPagination>(defaultPagination);

  const [draftFilters, setDraftFilters] = useState<KioskTransactionsDraftFilters>({
    kioskId: "",
    status: "",
    fromDate: "",
    toDate: "",
  });

  const [appliedFilters, setAppliedFilters] =
    useState<KioskTransactionsDraftFilters>(draftFilters);

  const [loadingFilters, setLoadingFilters] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(true);

  const [filtersError, setFiltersError] = useState<string | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [transactionsError, setTransactionsError] = useState<string | null>(null);

  const apiFilters = useMemo(
    () => toApiFilters(appliedFilters),
    [appliedFilters],
  );

  const loadKiosks = useCallback(async () => {
    try {
      setLoadingFilters(true);
      setFiltersError(null);
      const list = await getKiosks();
      setKiosks(list || []);
    } catch (error: any) {
      setFiltersError(error?.message || "Failed to load kiosks");
    } finally {
      setLoadingFilters(false);
    }
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      setLoadingSummary(true);
      setSummaryError(null);
      const result = await getKioskTransactionsSummary(apiFilters);
      setSummary(result);
    } catch (error: any) {
      setSummaryError(error?.message || "Failed to load summary data");
      setSummary(emptySummary);
    } finally {
      setLoadingSummary(false);
    }
  }, [apiFilters]);

  const loadTransactions = useCallback(async () => {
    try {
      setLoadingTransactions(true);
      setTransactionsError(null);
      const result = await getKioskTransactionsHistory(
        apiFilters,
        pagination.page,
        pagination.limit,
      );
      setTransactions(result.rows);
      setPagination(result.pagination);
    } catch (error: any) {
      setTransactionsError(error?.message || "Failed to load transactions");
      setTransactions([]);
      setPagination((previous) => ({
        ...previous,
        total: 0,
        totalPages: 1,
      }));
    } finally {
      setLoadingTransactions(false);
    }
  }, [apiFilters, pagination.page, pagination.limit]);

  useEffect(() => {
    loadKiosks();
  }, [loadKiosks]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  const applyFilters = useCallback(() => {
    setAppliedFilters(draftFilters);
    setPagination((previous) => ({
      ...previous,
      page: 1,
    }));
  }, [draftFilters]);

  const resetFilters = useCallback(() => {
    const resetState = {
      kioskId: "",
      status: "" as "",
      fromDate: "",
      toDate: "",
    };

    setDraftFilters(resetState);
    setAppliedFilters(resetState);
    setPagination((previous) => ({
      ...previous,
      page: 1,
    }));
  }, []);

  const updatePage = useCallback((page: number) => {
    setPagination((previous) => ({
      ...previous,
      page,
    }));
  }, []);

  const updateLimit = useCallback((limit: number) => {
    setPagination((previous) => ({
      ...previous,
      page: 1,
      limit,
    }));
  }, []);

  const refreshData = useCallback(async () => {
    await Promise.all([loadKiosks(), loadSummary(), loadTransactions()]);
  }, [loadKiosks, loadSummary, loadTransactions]);

  return {
    kiosks,
    summary,
    transactions,
    pagination,
    draftFilters,
    appliedFilters,
    setDraftFilters,
    applyFilters,
    resetFilters,
    updatePage,
    updateLimit,
    refreshData,
    loadingFilters,
    loadingSummary,
    loadingTransactions,
    filtersError,
    summaryError,
    transactionsError,
  };
};
